import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Copy, HeartPulse, Plus, Trash2, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import {
  createServer,
  deleteServer,
  listServers,
  refreshServerHealth,
  sendServerTestEvent,
  subscribeServer,
  type CreateServerInput,
  type ServerDto,
} from '../api/servers.api';
import { Spinner } from '../components/Spinner';
import { ConfirmDialog, useConfirm } from '../components/ConfirmDialog';
import type { Vendor } from '@ixam/shared';

// IXAM: estrae il messaggio d'errore dall'AppError standard del backend.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function apiErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.error?.message ?? fallback;
}

// IXAM: vendor con connector backend implementato (Subscribe/Test/Health).
const VENDORS_WITH_CONNECTOR = new Set<ServerDto['vendor']>(['dell', 'hp', 'lenovo']);

export function Servers() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const confirm = useConfirm();

  const { data: servers, isLoading } = useQuery({
    queryKey: ['servers'],
    queryFn: listServers,
  });

  const create = useMutation({
    mutationFn: createServer,
    onSuccess: (server) => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
      setShowAdd(false);
      toast.success(`Server "${server.name}" creato`);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Creazione non riuscita')),
  });

  const del = useMutation({
    mutationFn: deleteServer,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
      toast.success('Server eliminato');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Eliminazione non riuscita')),
  });

  const subscribe = useMutation({
    mutationFn: subscribeServer,
    onSuccess: (result, id) => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      const server = servers?.find((s) => s._id === id);
      const what = result.alreadyExisted ? 'già presente' : 'creata';
      toast.success(`Sottoscrizione ${what} su ${server?.name ?? id}`, {
        description: `Subscription ID: ${result.subscriptionId}`,
      });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Sottoscrizione non riuscita')),
  });

  const testEvent = useMutation({
    mutationFn: sendServerTestEvent,
    onSuccess: (result, id) => {
      const server = servers?.find((s) => s._id === id);
      if (result.ok) {
        toast.success(`Evento di test inviato a ${server?.name ?? id}`, {
          description: 'Controlla i canali di notifica entro qualche secondo.',
        });
      } else {
        toast.error('Evento di test fallito', {
          description: result.error ?? undefined,
        });
      }
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Evento di test fallito')),
  });

  const refreshHealth = useMutation({
    mutationFn: refreshServerHealth,
    onSuccess: ({ result, server }) => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'stats'] });
      toast.success(`Salute ${server.name}: ${result.status}`, {
        description: result.raw ? `BMC: ${result.raw}` : undefined,
      });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'BMC non raggiungibile')),
  });

  return (
    <div className="p-5 md:p-10 max-w-7xl mx-auto space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-bold text-3xl text-ink">Server</h1>
          <p className="text-sm text-ink-muted mt-1">
            Hardware monitorato — Dell iDRAC, HP iLO, Lenovo XClarity.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} className="ixam-btn">
          <Plus className="w-4 h-4" />
          Aggiungi server
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-3 text-ink-muted">
          <Spinner size={20} /> Caricamento…
        </div>
      ) : (
        <div className="ixam-card overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-paper-soft text-xs uppercase tracking-wide text-ink-faint">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Nome</th>
                <th className="text-left px-5 py-3 font-medium">Vendor</th>
                <th className="text-left px-5 py-3 font-medium">Host BMC</th>
                <th className="text-left px-5 py-3 font-medium">Salute</th>
                <th className="text-left px-5 py-3 font-medium">URL webhook</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-soft">
              {servers?.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-ink-muted">
                    Nessun server. Aggiungine uno per iniziare.
                  </td>
                </tr>
              )}
              {servers?.map((s) => (
                <tr key={s._id} className="hover:bg-paper-soft/40">
                  <td className="px-5 py-3.5 font-medium text-ink">
                    {s.name}
                    {s.bmc.subscriptionId ? (
                      <div className="text-xs text-ink-faint font-mono mt-0.5">
                        sub: {s.bmc.subscriptionId}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-5 py-3.5 text-sm capitalize">{s.vendor}</td>
                  <td className="px-5 py-3.5 font-mono text-sm text-ink-muted">{s.bmc.host}</td>
                  <td className="px-5 py-3.5">
                    <HealthBadge status={s.health.status} />
                  </td>
                  <td className="px-5 py-3.5 max-w-md">
                    <WebhookCell url={s.webhookUrl} />
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      {VENDORS_WITH_CONNECTOR.has(s.vendor) && (
                        <>
                          <button
                            title="Sottoscrivi gli eventi Redfish del BMC"
                            onClick={() => subscribe.mutate(s._id)}
                            disabled={subscribe.isPending}
                            className="ixam-icon-btn text-accent hover:text-accent-hover"
                          >
                            <Wifi className="w-4 h-4" />
                          </button>
                          <button
                            title="Aggiorna stato salute dal BMC"
                            onClick={() => refreshHealth.mutate(s._id)}
                            disabled={refreshHealth.isPending}
                            className="ixam-icon-btn text-sev-info"
                          >
                            <HeartPulse className="w-4 h-4" />
                          </button>
                          <button
                            title="Invia un evento di test dal BMC"
                            onClick={() => testEvent.mutate(s._id)}
                            disabled={testEvent.isPending || !s.bmc.subscriptionId}
                            className="ixam-icon-btn text-sev-warning"
                          >
                            <BellRing className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() =>
                          confirm.ask({
                            title: `Eliminare "${s.name}"?`,
                            body: 'Il server verrà rimosso da IXAM. La sottoscrizione su iDRAC e gli eventi storici associati restano in DB.',
                            confirmLabel: 'Elimina',
                            destructive: true,
                            onConfirm: () => del.mutate(s._id),
                          })
                        }
                        className="ixam-icon-btn text-sev-critical"
                        title="Elimina server"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddServerModal
          onClose={() => setShowAdd(false)}
          onSave={(input) => create.mutate(input)}
          loading={create.isPending}
          error={create.error}
        />
      )}

      <ConfirmDialog request={confirm.request} onClose={confirm.dismiss} />
    </div>
  );
}

function HealthBadge({ status }: { status: ServerDto['health']['status'] }) {
  const colors: Record<ServerDto['health']['status'], string> = {
    ok: 'bg-sev-ok-soft text-sev-ok border-sev-ok/30',
    warning: 'bg-sev-warning-soft text-sev-warning border-sev-warning/30',
    critical: 'bg-sev-critical-soft text-sev-critical border-sev-critical/30',
    unknown: 'bg-paper-soft text-ink-faint border-line',
  };
  const labels: Record<ServerDto['health']['status'], string> = {
    ok: 'ok',
    warning: 'attenzione',
    critical: 'critico',
    unknown: 'sconosciuto',
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-medium rounded-md border ${colors[status]}`}
    >
      {labels[status]}
    </span>
  );
}

function WebhookCell({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="flex items-center gap-2 text-xs font-mono text-ink-faint hover:text-ink max-w-full transition-colors"
      title={url}
    >
      <Copy className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">{copied ? 'Copiato!' : url}</span>
    </button>
  );
}

function AddServerModal({
  onClose,
  onSave,
  loading,
  error,
}: {
  onClose: () => void;
  onSave: (s: CreateServerInput) => void;
  loading: boolean;
  error: unknown;
}) {
  const [form, setForm] = useState<CreateServerInput>({
    name: '',
    vendor: 'dell',
    model: '',
    bmc: { type: 'idrac', host: '', username: '', password: '' },
  });

  function setVendor(vendor: Vendor) {
    const bmcType: 'idrac' | 'ilo' | 'xcc' =
      vendor === 'dell' ? 'idrac' : vendor === 'hp' ? 'ilo' : 'xcc';
    setForm((prev) => ({ ...prev, vendor, bmc: { ...prev.bmc, type: bmcType } }));
  }

  function update<K extends keyof CreateServerInput>(k: K, v: CreateServerInput[K]) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  function updateBmc(field: 'host' | 'username' | 'password', v: string) {
    setForm((prev) => ({ ...prev, bmc: { ...prev.bmc, [field]: v } }));
  }

  const errMsg = error
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((error as any)?.response?.data?.error?.message ?? 'Salvataggio non riuscito')
    : null;

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave(form);
  }

  return (
    <Modal title="Nuovo server" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nome">
          <input
            required
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="srv-prod-01"
            className="ixam-input"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vendor">
            <select
              value={form.vendor}
              onChange={(e) => setVendor(e.target.value as Vendor)}
              className="ixam-input"
            >
              <option value="dell">Dell (iDRAC)</option>
              <option value="hp">HP (iLO)</option>
              <option value="lenovo">Lenovo (XCC)</option>
            </select>
          </Field>
          <Field label="Modello">
            <input
              value={form.model}
              onChange={(e) => update('model', e.target.value)}
              placeholder="PowerEdge R740xd"
              className="ixam-input"
            />
          </Field>
        </div>
        <Field label="Host BMC (IP o DNS)">
          <input
            required
            value={form.bmc.host}
            onChange={(e) => updateBmc('host', e.target.value)}
            placeholder="172.16.25.14"
            className="ixam-input"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Utente BMC">
            <input
              required
              value={form.bmc.username}
              onChange={(e) => updateBmc('username', e.target.value)}
              placeholder="root"
              className="ixam-input"
            />
          </Field>
          <Field label="Password BMC">
            <input
              required
              type="password"
              value={form.bmc.password}
              onChange={(e) => updateBmc('password', e.target.value)}
              className="ixam-input"
            />
          </Field>
        </div>
        {errMsg && (
          <div className="px-3 py-2 bg-sev-critical-soft border border-sev-critical/30 rounded-lg text-xs text-sev-critical">
            {errMsg}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="ixam-btn-ghost flex-1 justify-center">
            Annulla
          </button>
          <button type="submit" disabled={loading} className="ixam-btn flex-1 justify-center">
            {loading ? 'Salvataggio…' : 'Salva server'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-paper-card border border-line-soft rounded-2xl shadow-soft p-7 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-bold text-xl text-ink mb-5">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-ink mb-1.5">{label}</div>
      {children}
    </label>
  );
}
